import { all, call, put, takeLatest } from 'redux-saga/effects';

import axios from 'axios';
import { CLIENTS } from 'app/api-routes';
import { TABLE_NAMES } from 'app/types/constants';

import {
  clientsRequest,
  clientsSuccess,
  clientsFailure,

  clientRequest,
  clientSuccess,
  clientFailure,

  updateClientStatusRequest,
  updateClientStatusSuccess,
  updateClientStatusFailure,

  createClientRequest,
  createClientFailure,

  updateClientRequest,
  updateClientFailure,

  deleteClientRequest,
  deleteClientFailure,
  updateClientSuccess,
  deleteClientSuccess,
  copyClientRequest,
  copyClientSuccess,
  copyClientFailure,
} from './actions';

function* clientsSaga() {
  try {
    const { payload, warnings } = yield axios.get(CLIENTS);

    yield put(clientsSuccess(payload, { warnings }));
  } catch (err) {
    yield put(clientsFailure(err));
  }
}

function* getClientSaga({ payload: clientID }) {
  try {
    const { payload, warnings } = yield axios.get(`${CLIENTS}/${clientID}`);

    yield put(clientSuccess(payload, { warnings }));
  } catch (err) {
    yield put(clientFailure(err));
  }
}

function* updateClientStatusSaga({ payload: { clientID, status } }) {
  try {
    yield axios.put(`${CLIENTS}/${clientID}/status/${status}`);

    yield put(updateClientStatusSuccess({ status, clientID }));
  } catch (err) {
    yield put(updateClientStatusFailure(err));
  }
}

function* createClientSaga({ payload: { data, onSuccess, onFailure } }) {
  try {
    const { payload: id } = yield axios.post(CLIENTS, {
      payload: data,
    });
    
    onSuccess?.(id);

  } catch (err) {
    onFailure?.();
    yield put(createClientFailure(err));
  }
}

function* updateClientSaga({ payload: { clientID, data, onSuccess, onFailure } }) {
  try {
    yield axios.put(`${CLIENTS}/${clientID}`, {
      payload: data,
    });

    onSuccess?.();
    yield call(getClientSaga, { payload: clientID });
    yield put(updateClientSuccess({ clientID, data }));
  } catch (err) {
    onFailure?.();
    yield put(updateClientFailure(err));
  }
}

function* deleteClientSaga({ payload: { clientID, callback } }) {
  try {
    yield axios.delete(`${CLIENTS}/${clientID}`);
    callback?.();
    yield put(deleteClientSuccess(clientID));
  } catch (err) {
    yield put(deleteClientFailure(err));
  }
}

function* copyClientReportsSaga(sourceClientID, newClientID) {
  const { payload: settings } = yield axios.get(`${CLIENTS}/${sourceClientID}/email-settings`);

  for (const setting of settings || []) {
    if (!setting?.enabled) continue;

    yield axios.put(`${CLIENTS}/${newClientID}/email-settings/${setting.type}/enable`);

    const { payload: recipients } = yield axios.get(`${CLIENTS}/${sourceClientID}/email-recipients`, {
      params: { type: setting.type },
    });

    const carried = (recipients || [])
      .filter(({ tableName }) => tableName !== TABLE_NAMES.CONTACTS)
      .map(({ tableName, entityID }) => ({ tableName, entityID }));

    if (carried.length) {
      yield axios.post(
        `${CLIENTS}/${newClientID}/email-recipients`,
        { payload: carried },
        { params: { type: setting.type } }
      );
    }
  }
}

function* copyClientSaga({ payload: { clientID, name, options, defaultCountryID, onSuccess, onFailure } }) {
  try {
    const { payload: source } = yield axios.get(`${CLIENTS}/${clientID}`);

    const data = {
      clientName: name,
      dateAmended: null,
      contacts: [],
      accountManagerID: source.accountManagerID,
      countryID: options?.address ? source.countryID : defaultCountryID ?? source.countryID,
      notes: options?.clientProfile ? source.notes || [] : [],
      ...(options?.info
        ? {
            phone1: source.phone1,
            phone2: source.phone2,
            fax: source.fax,
            email: source.email,
            webSite: source.webSite,
          }
        : {}),
      ...(options?.address
        ? {
            addressLine1: source.addressLine1,
            addressLine2: source.addressLine2,
            city: source.city,
            province: source.province,
            postalCode: source.postalCode,
          }
        : {}),
    };

    const { payload: newClientID } = yield axios.post(CLIENTS, { payload: data });

    let reportsCopied = true;

    if (options?.reports) {
      try {
        yield call(copyClientReportsSaga, clientID, newClientID);
      } catch (err) {
        reportsCopied = false;
      }
    }

    onSuccess?.(newClientID, { reportsCopied });
    yield put(copyClientSuccess({ clientID: newClientID }));
  } catch (err) {
    onFailure?.(err);
    yield put(copyClientFailure(err));
  }
}

export default function*() {
  yield all([
    yield takeLatest(clientsRequest, clientsSaga),
    yield takeLatest(clientRequest, getClientSaga),
    yield takeLatest(updateClientStatusRequest, updateClientStatusSaga),
    yield takeLatest(createClientRequest, createClientSaga),
    yield takeLatest(updateClientRequest, updateClientSaga),
    yield takeLatest(deleteClientRequest, deleteClientSaga),
    yield takeLatest(copyClientRequest, copyClientSaga),
  ]);
}
